import * as React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import useFetch from 'use-http';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import ARNavBar from './ARNavBar';
import IccoBreakdownSearch from './components/IccoBreakdownSearch';
import IccoExcelReportDialog, { IIccoExcelReportParams, IccoReportType } from './dialogs/IccoExcelReportDialog';
import IccoMultiOfficeChecksDialog, { IIccoMultiOfficeChecksParams } from './dialogs/IccoMultiOfficeChecksDialog';
import IccoInvoiceOfficeBreakdown from './models/iccoInvoiceOfficeBreakdown';
import IccoContractPayOfficeBreakdown from './models/iccoContractPayOfficeBreakdown';
import { IIccoBreakdownSearchCriteria, useArServiceCtx } from './AccountsReceivableServiceContext';
import './ArHistoryPage.module.scss';

const formatCurrency = (value: number) =>
    (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Mirrors icco_brkdwn.fmt's ICCO canvas/window - QUERY_DATA [oid 24] drives both the
// "Invoice Breakdown by Office" (block INVOICE_OFFICE) and "Contractor Paychecks by Office"
// (block CONTRACT_PAY_OFFICE) grids off one Query click, each with its own running total
// (Oracle's own :control.tot/:control.amount client-side accumulation loops - computed here
// the same way te_nonrecon's own grids total their columns, via reduce).
const IccoBreakdownPage = () => {
    const dispatch = useDispatch();
    const { getIccoBreakdown } = useArServiceCtx();
    // Separate instance so this GET doesn't race/abort against the grids' own fetch on the
    // service context's shared instance (same reasoning as ArHistoryPage.tsx's own getReport).
    const { get: getReport, response: reportResponse } = useFetch();
    // Separate instance from getReport, for the same reason getReport is separate from the
    // grids' own fetch - independent dialogs shouldn't race/abort each other's requests.
    const { get: getMoReport, response: moReportResponse } = useFetch();
    const [invoiceOffice, setInvoiceOffice] = useState<Array<IccoInvoiceOfficeBreakdown>>([]);
    const [contractPayOffice, setContractPayOffice] = useState<Array<IccoContractPayOfficeBreakdown>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [hasQueried, setHasQueried] = useState<boolean>(false);
    // Beg/End Date as of the last time Query or Excel Report was clicked - both the Excel dialog
    // and the eventual report call need this (mirrors :control.beg_date/:control.end_date being
    // read directly off the Control block by both B_QUERY and B_EXCEL/XL_Icco_Reports).
    const [reportCriteria, setReportCriteria] = useState<IIccoBreakdownSearchCriteria | undefined>(undefined);
    const [reportMessage, setReportMessage] = useState<string>('');
    const [reportGenerating, setReportGenerating] = useState<boolean>(false);
    const [moMessage, setMoMessage] = useState<string>('');
    const [moGenerating, setMoGenerating] = useState<boolean>(false);

    const runQuery = async (criteria: IIccoBreakdownSearchCriteria) => {
        setLoading(true);
        // getIccoBreakdown's declared return type includes `void` (the context's shared getter
        // shape) - optional chaining only excludes null/undefined, not void, so TS still flags
        // property access below without this cast to the shape the endpoint actually returns.
        const result = await getIccoBreakdown(criteria) as {
            invoiceOffice?: Array<IccoInvoiceOfficeBreakdown>;
            contractPayOffice?: Array<IccoContractPayOfficeBreakdown>;
        } | undefined;
        setInvoiceOffice(Array.isArray(result?.invoiceOffice) ? result.invoiceOffice : []);
        setContractPayOffice(Array.isArray(result?.contractPayOffice) ? result.contractPayOffice : []);
        setHasQueried(true);
        setLoading(false);
    };

    const hideDialog = () => dispatch(actions.layout.hideDialog());

    // Mirrors WHEN-BUTTON-PRESSED [oid 42] - opens the Excel Report popup (Go_Block('Excel')).
    const handleOpenExcelReport = (criteria: IIccoBreakdownSearchCriteria) => {
        setReportCriteria(criteria);
        setReportMessage('');
        dispatch(actions.layout.showDialog(DialogList.ICCO_EXCEL_REPORT));
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 50] - opens the Multi-Office Checks popup (Go_Block('MO_EXCEL')).
    const handleOpenMultiOfficeChecks = () => {
        setMoMessage('');
        dispatch(actions.layout.showDialog(DialogList.ICCO_MULTI_OFFICE_CHECKS));
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 153] -> XL_MultiOffice_Checks, ported off Oracle's native
    // ORA_EXCEL build into usp_MultiOfficeChecksReport + NPOI (no .rdf/.rdl ever existed for it).
    const handleGenerateMultiOfficeChecks = async (params: IIccoMultiOfficeChecksParams) => {
        setMoGenerating(true);
        setMoMessage('');

        const url = `/v1.0/AccountsReceivable/icco/multi-office-checks?${new URLSearchParams({
            begPeriod: params.begPeriod.toString(),
            endPeriod: params.endPeriod.toString(),
        }).toString()}`;

        await getMoReport(url);

        if (!moReportResponse.ok) {
            setMoMessage('Unable to generate the report. Please try again.');
            setMoGenerating(false);
            return;
        }

        const blob = await moReportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = params.filename;
        a.click();
        window.URL.revokeObjectURL(blobUrl);

        setMoGenerating(false);
        hideDialog();
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 80] -> XL_Icco_Reports. Each of the 8 report types is
    // already deployed as its own SSRS RDL report - wired up one at a time as each gets built,
    // rather than guessing at report names/parameters for all 8 up front. reportCriteria (beg/end
    // date/type) is already threaded through here so each report only needs its own URL/params
    // added when its turn comes. Every wired-up report shares the same type/begDate/endDate -
    // office is added only for the ones whose stored proc actually takes it (confirmed per-report
    // via its own deployed RDL/proc, not assumed).
    const buildReportUrl = (reportType: IccoReportType, criteria: IIccoBreakdownSearchCriteria, office: string): string | undefined => {
        const base = { type: criteria.type, begDate: criteria.begDate, endDate: criteria.endDate };
        switch (reportType) {
            case 'IOB':
                return `/v1.0/reports/GetReport/InvoiceOfficeBreakdown?${new URLSearchParams(base).toString()}`;
            case 'IOS':
                return `/v1.0/reports/GetReport/InvoiceStateBreakdown?${new URLSearchParams({ ...base, office: office ?? '' }).toString()}`;
            case 'CPOB':
                return `/v1.0/reports/GetReport/ContractorPayOfficeBreakdown?${new URLSearchParams(base).toString()}`;
            case 'CPOBPP':
                return `/v1.0/reports/GetReport/ContractorPayOfficeBreakdownPP?${new URLSearchParams(base).toString()}`;
            case 'ICPOB':
                return `/v1.0/reports/GetReport/InvoiceContractorPayOfficeBreakdown?${new URLSearchParams(base).toString()}`;
            case 'CS':
                // usp_ContractorSummaryReport takes only begDate/endDate/office - no rpt_id, no type.
                return `/v1.0/reports/GetReport/ContractorSummary?${new URLSearchParams({ begDate: criteria.begDate, endDate: criteria.endDate, office: office ?? '' }).toString()}`;
            case 'IBOC':
                return `/v1.0/reports/GetReport/InvoiceOfficeClientBreakdown?${new URLSearchParams(base).toString()}`;
            case 'INVCOMP':
                return `/v1.0/reports/GetReport/InvoiceComparison?${new URLSearchParams({ ...base, office: office ?? '' }).toString()}`;
            default:
                return undefined;
        }
    };

    const handleGenerateReport = async (params: IIccoExcelReportParams) => {
        if (!reportCriteria) {
            return;
        }

        const url = buildReportUrl(params.reportType, reportCriteria, params.office);
        if (!url) {
            setReportMessage(`Report generation for "${params.reportType}" is not wired up yet.`);
            return;
        }

        setReportGenerating(true);
        setReportMessage('');

        await getReport(url);

        if (!reportResponse.ok) {
            setReportMessage('Unable to generate the report. Please try again.');
            setReportGenerating(false);
            return;
        }

        const blob = await reportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = params.filename;
        a.click();
        window.URL.revokeObjectURL(blobUrl);

        setReportGenerating(false);
        hideDialog();
    };

    const invoiceOfficeTotal = invoiceOffice.reduce((sum, item) => sum + (item.total ?? 0), 0);
    const contractPayOfficeTotal = contractPayOffice.reduce((sum, item) => sum + (item.amount ?? 0), 0);

    return (
        <div>
            <ARNavBar />
            <IccoBreakdownSearch runQuery={runQuery} onExcelReport={handleOpenExcelReport} onMultiOfficeChecks={handleOpenMultiOfficeChecks} />
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={6}>
                    <div style={{ paddingRight: '.5rem' }}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel panelTitle="Invoice Breakdown by Office">
                                <div style={{ overflowY: 'scroll', maxHeight: '32rem' }}>
                                    <table style={{ width: '100%' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '12%' }}>Office</th>
                                                <th style={{ width: '15%' }}>Services</th>
                                                <th style={{ width: '13%' }}>BOP</th>
                                                <th style={{ width: '15%' }}>Expenses</th>
                                                <th style={{ width: '15%' }}>Off. Expenses</th>
                                                <th style={{ width: '15%' }}>Other</th>
                                                <th style={{ width: '15%' }}>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading && (
                                                <tr>
                                                    <td colSpan={7} className="bold-centered"><LoadingSpinner /></td>
                                                </tr>
                                            )}
                                            {!loading && invoiceOffice.map((item, index) => (
                                                <tr key={`${item.office}-${item.rptType}-${index}`} style={{ textAlign: 'center' }}>
                                                    <td className="arHistoryCell">{item.office}</td>
                                                    <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(item.services)}</td>
                                                    <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(item.bop)}</td>
                                                    <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(item.expenses)}</td>
                                                    <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(item.offExpenses)}</td>
                                                    <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(item.fieldEquipment)}</td>
                                                    <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(item.total)}</td>
                                                </tr>
                                            ))}
                                            {!loading && hasQueried && invoiceOffice.length === 0 && (
                                                <tr><td colSpan={7} className="bold-centered">No Data Found</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="form-row" style={{ justifyContent: 'center', alignItems: 'center', marginTop: '1rem', gap: '1rem' }}>
                                    <span style={{ fontWeight: 'bold' }}>Total</span>
                                    <span style={{ backgroundColor: '#8EE08E', padding: '.5rem 1.5rem', fontWeight: 'bold' }}>{formatCurrency(invoiceOfficeTotal)}</span>
                                </div>
                            </Panel>
                        </Panel>
                    </div>
                </Page.Section>
                <Page.Section width={6}>
                    <div style={{ paddingLeft: '.5rem' }}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel panelTitle="Contractor Paychecks by Office">
                                <div style={{ overflowY: 'scroll', maxHeight: '32rem' }}>
                                    <table style={{ width: '100%' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '15%' }}>Office</th>
                                                <th style={{ width: '17%' }}>Services</th>
                                                <th style={{ width: '17%' }}>Expenses</th>
                                                <th style={{ width: '17%' }}>W/C</th>
                                                <th style={{ width: '17%' }}>Withhold</th>
                                                <th style={{ width: '17%' }}>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading && (
                                                <tr>
                                                    <td colSpan={6} className="bold-centered"><LoadingSpinner /></td>
                                                </tr>
                                            )}
                                            {!loading && contractPayOffice.map((item, index) => (
                                                <tr key={`${item.office}-${index}`} style={{ textAlign: 'center' }}>
                                                    <td className="arHistoryCell">{item.office}</td>
                                                    <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(item.totService)}</td>
                                                    <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(item.totExpense)}</td>
                                                    <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(item.totWc)}</td>
                                                    <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(item.totNonWc)}</td>
                                                    <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(item.amount)}</td>
                                                </tr>
                                            ))}
                                            {!loading && hasQueried && contractPayOffice.length === 0 && (
                                                <tr><td colSpan={6} className="bold-centered">No Data Found</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="form-row" style={{ justifyContent: 'center', alignItems: 'center', marginTop: '1rem', gap: '1rem' }}>
                                    <span style={{ fontWeight: 'bold' }}>Total</span>
                                    <span style={{ backgroundColor: '#8EE08E', padding: '.5rem 1.5rem', fontWeight: 'bold' }}>{formatCurrency(contractPayOfficeTotal)}</span>
                                </div>
                            </Panel>
                        </Panel>
                    </div>
                </Page.Section>
            </Page>
            <IccoExcelReportDialog
                dialogName={DialogList.ICCO_EXCEL_REPORT}
                hideDialog={hideDialog}
                onGenerateReport={handleGenerateReport}
                message={reportMessage}
                generating={reportGenerating}
            />
            <IccoMultiOfficeChecksDialog
                dialogName={DialogList.ICCO_MULTI_OFFICE_CHECKS}
                hideDialog={hideDialog}
                onGenerateReport={handleGenerateMultiOfficeChecks}
                message={moMessage}
                generating={moGenerating}
            />
        </div>
    );
};

export default IccoBreakdownPage;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/IccoBreakdownPage.tsx